/**
 * Незакрытые разговоры и запрет набора без метки.
 *
 * Правило смены: по каждому завершённому звонку метка обязательна, и пока её
 * нет — следующий номер не набирается. Введено 29.08.2026 после смены, где
 * девять реальных разговоров остались без результата.
 *
 * «Разговор был» здесь означает «разговор дошёл до диспетчера» (talkedCnt по
 * звонкам с is_reached): карусельный сброс — абонент снял, пульт не подхватил —
 * метку не требует, диспетчер этого звонка не видел (правка 01.09). Назначения
 * в needs_label (перевод expire-крона) считаются незакрытыми всегда.
 *
 * Функции чистые: желание показать блок и заблокировать кнопки — решение
 * экрана, а само правило должно проверяться тестом без React.
 */

import type { DeskAssignment } from '@cc/shared';

export type UnlabeledSource = Pick<DeskAssignment, 'id' | 'status' | 'talkedCnt'>;

/**
 * Назначения, по которым звонок был, а метки нет — в порядке сервера
 * (needs_label первыми). suppressedIds — метки, уже принятые сервером в этой
 * сессии: снапшот обновляется с задержкой, и без исключения только что
 * размеченная карточка на секунду «вернулась» бы как незакрытая.
 */
export function unlabeledAssignments<A extends UnlabeledSource>(
  assignments: readonly A[],
  suppressedIds: ReadonlySet<string>,
): A[] {
  return assignments.filter(
    (a) =>
      !suppressedIds.has(a.id) &&
      (a.status === 'needs_label' || (a.status === 'called' && a.talkedCnt > 0)),
  );
}

/**
 * Можно ли набирать эту карточку.
 *
 * Свою незакрытую — можно: разговор оборвался, диспетчер перезванивает тому же
 * человеку до простановки метки. Любая другая незакрытая блокирует набор.
 */
export function dialAllowed(
  targetId: string,
  unlabeled: readonly UnlabeledSource[],
): boolean {
  return unlabeled.every((a) => a.id === targetId);
}

export type DeskOrderSource = UnlabeledSource &
  Pick<DeskAssignment, 'assignedAt'> & {
    /** Конец последнего разговора; нет поля — старый api (окно деплоя) */
    lastTalkAt?: string | null | undefined;
  };

/**
 * Порядок карточек на пульте (28.09.2026): разговоры без метки — наверх,
 * самый свежий первым, остальные — в порядке сервера.
 *
 * Инцидент: Катя поговорила с клиентом 2:56, страница перезагрузилась, и
 * карточка с незакрытым разговором оказалась одной из 40 где-то в середине
 * списка — найти её не смогла. Экран берёт активной первую карточку списка,
 * поэтому после F5 диспетчер сразу попадает на неё.
 *
 * «Разговор был» — то же правило, что запрещает набор (unlabeledAssignments):
 * закреплённая карточка и блокировка набора не расходятся. Сырой talk_sec
 * здесь не годится: за 14 дней 713 карточек с talk_sec > 0 без is_reached —
 * автоответчики и карусельные сбросы, разговора с диспетчером там не было.
 */
export function orderForDesk<A extends DeskOrderSource>(
  assignments: readonly A[],
  suppressedIds: ReadonlySet<string>,
): A[] {
  const pinned = unlabeledAssignments(assignments, suppressedIds);
  if (pinned.length === 0) return [...assignments];
  const pinnedIds = new Set(pinned.map((a) => a.id));
  // Без времени разговора (старый api) — по моменту выдачи
  const recency = (a: A): number => Date.parse(a.lastTalkAt ?? a.assignedAt);
  const sorted = [...pinned].sort((x, y) => recency(y) - recency(x));
  return [...sorted, ...assignments.filter((a) => !pinnedIds.has(a.id))];
}

/**
 * Кому ставится результат. Приоритет: карточка звонка на экране, затем
 * выбранная карточка, если это сама незакрытая, затем первый незакрытый
 * разговор, затем выбранная (метку можно ставить и без звонка — например,
 * когда договорились в переписке).
 *
 * Выбранная незакрытая идёт раньше первой (ревью 28.09.2026): при двух
 * разговорах без метки диспетчер кликает вторую — слева открывается она, и
 * результат, заполненный по её разговору, должен уйти ей, а не первой.
 */
export function labelTargetFor<A extends UnlabeledSource>(
  callAssignment: A | null,
  active: A | null,
  unlabeled: readonly A[],
): A | null {
  if (callAssignment !== null) return callAssignment;
  if (active !== null && unlabeled.some((a) => a.id === active.id)) return active;
  return unlabeled[0] ?? active;
}
